import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3013/api";
// Extract base URL (without /api) for image URLs
const API_BASE = API_URL.replace(/\/api$/, '');

// Log API URL on load for debugging
console.log("📺 Slideshow API URL:", API_URL);
console.log("📺 Slideshow API BASE (for images):", API_BASE);

// Fix image URLs that might have localhost or wrong host
const fixImageUrl = (url) => {
  if (!url) return url;
  
  // If URL contains /api/uploads/, extract the path and use our API base
  const uploadsMatch = url.match(/\/api\/uploads\/(.+)$/);
  if (uploadsMatch) {
    const fixedUrl = `${API_BASE}/api/uploads/${uploadsMatch[1]}`;
    console.log("📺 Fixed image URL:", url, "->", fixedUrl);
    return fixedUrl;
  }
  
  // If it's already a relative URL, prepend the API base
  if (url.startsWith('/api/uploads/')) {
    return `${API_BASE}${url}`;
  }
  
  return url;
};

const Slideshow = () => {
  const [mainboard, setMainboard] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedImages, setLoadedImages] = useState({}); // Track loaded images
  const [isLoading, setIsLoading] = useState(true);
  const timerRef = useRef(null);
  const slidesRef = useRef([]);
  const loadedImagesRef = useRef({}); // Ref for accessing in callbacks
  
  // Keep ref in sync with state
  useEffect(() => {
    loadedImagesRef.current = loadedImages;
  }, [loadedImages]);

  // Preload an image and return a promise
  const preloadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => reject(url);
      img.src = url;
    });
  };

  // Preload only NEW images (not already loaded)
  const preloadAllImages = useCallback(async (slides, existingLoadedImages) => {
    const imageSlides = slides.filter(
      (slide) => slide.displayType === 'image' && slide.imageUrl
    );

    if (imageSlides.length === 0) {
      setIsLoading(false);
      return;
    }

    // Filter to only NEW images that haven't been loaded yet
    const newImageSlides = imageSlides.filter(
      (slide) => existingLoadedImages[slide.imageUrl] === undefined
    );

    if (newImageSlides.length === 0) {
      console.log("📺 No new images to preload, all", imageSlides.length, "images already cached");
      setIsLoading(false);
      return;
    }

    console.log("📺 Preloading", newImageSlides.length, "NEW images (", imageSlides.length - newImageSlides.length, "already cached):");
    newImageSlides.forEach((slide, i) => {
      const fixedUrl = fixImageUrl(slide.imageUrl);
      console.log(`   ${i + 1}. ${fixedUrl}`);
    });
    
    // Only show loading on first load
    if (Object.keys(existingLoadedImages).length === 0) {
      setIsLoading(true);
    }

    const results = {};
    await Promise.all(
      newImageSlides.map(async (slide) => {
        const fixedUrl = fixImageUrl(slide.imageUrl);
        try {
          await preloadImage(fixedUrl);
          // Store with original URL as key for lookup
          results[slide.imageUrl] = true;
          console.log("✅ Image loaded:", fixedUrl);
        } catch (error) {
          results[slide.imageUrl] = false;
          console.error("❌ Image FAILED to load:", fixedUrl, "- Check if URL is accessible");
        }
      })
    );

    setLoadedImages((prev) => ({ ...prev, ...results }));
    setIsLoading(false);
    console.log("📺 Image preloading complete");
  }, []);

  // Fetch mainboard data directly with polling
  const fetchMainboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/sab/mainboard`);
      const data = response.data || [];
      
      // Filter and sort
      const filteredData = data
        .filter((item) => item.isActive !== false)
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      
      setMainboard(filteredData);
      console.log("📺 Slideshow - fetched mainboard:", filteredData.length, "items @", new Date().toLocaleTimeString());
      
      // Preload only NEW images (pass current loaded images)
      await preloadAllImages(filteredData, loadedImagesRef.current);
    } catch (error) {
      console.error("❌ Slideshow - failed to fetch mainboard:", error);
      setIsLoading(false);
    }
  }, [preloadAllImages]);

  // Initial fetch + polling every 30 seconds
  useEffect(() => {
    fetchMainboard();
    const pollInterval = setInterval(fetchMainboard, 30 * 1000);
    return () => clearInterval(pollInterval);
  }, [fetchMainboard]);

  // Slides are already filtered and sorted in fetchMainboard
  const slides = mainboard;
  
  // Keep slides ref updated
  slidesRef.current = slides;

  // Auto-advance slideshow - runs continuously regardless of hover
  useEffect(() => {
    if (slides.length <= 1 || isLoading) return;

    const advanceSlide = () => {
      const currentSlides = slidesRef.current;
      if (currentSlides.length <= 1) return;
      
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % currentSlides.length);
        setIsTransitioning(false);
      }, 500);
    };

    // Get duration for current slide
    const currentSlide = slides[currentIndex];
    const duration = (currentSlide?.duration || 10) * 1000;

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set new timer - this will NOT be affected by hover/re-renders
    timerRef.current = setTimeout(advanceSlide, duration);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentIndex, slides.length, isLoading]);

  // Reset index if slides change
  useEffect(() => {
    if (currentIndex >= slides.length && slides.length > 0) {
      setCurrentIndex(0);
    }
  }, [slides.length, currentIndex]);

  const currentSlide = slides[currentIndex];
  const isImageSlide = currentSlide?.displayType === 'image' && currentSlide?.imageUrl;
  const isImageLoaded = isImageSlide ? loadedImages[currentSlide.imageUrl] === true : true;

  // Loading state - show while preloading images
  if (isLoading && slides.length > 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <span style={styles.emptyIcon}>☬</span>
          <h2 style={styles.emptyTitle}>ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ</h2>
          <h2 style={styles.emptyTitle}>ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਹਿ</h2>
          <p style={styles.emptyText}>Loading slides...</p>
        </div>
      </div>
    );
  }

  if (!slides.length) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <span style={styles.emptyIcon}>☬</span>
          <h2 style={styles.emptyTitle}>ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ</h2>
          <h2 style={styles.emptyTitle}>ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਹਿ</h2>
          <p style={styles.emptyText}>No announcements at this time</p>
        </div>
      </div>
    );
  }

  // Image Slide - only show if image is loaded
  if (isImageSlide) {
    // If image failed to load, skip to text display or show fallback
    if (!isImageLoaded) {
      return (
        <div style={styles.container}>
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>☬</span>
            <h2 style={styles.emptyTitle}>{currentSlide.title || 'ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ'}</h2>
            <p style={styles.emptyText}>Image loading...</p>
          </div>
        </div>
      );
    }

    // Use fixed URL for display
    const displayImageUrl = fixImageUrl(currentSlide.imageUrl);
    
    return (
      <div style={styles.imageContainer}>
        <img
          src={displayImageUrl}
          alt={currentSlide.title || "Slide"}
          style={{
            ...styles.fullImage,
            opacity: isTransitioning ? 0 : 1,
          }}
          onError={(e) => {
            console.error("❌ Image display error:", displayImageUrl);
            // Mark as not loaded so it shows fallback
            setLoadedImages((prev) => ({ ...prev, [currentSlide.imageUrl]: false }));
          }}
        />
      </div>
    );
  }

  // Text Slide
  return (
    <div style={styles.container}>
      {/* Title - Fixed at Top on the line */}
      <div
        style={{
          ...styles.titleContainer,
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? "translateY(-20px)" : "translateY(0)",
        }}
      >
        {currentSlide?.title && (
          <h1 style={styles.title}>{currentSlide.title}</h1>
        )}
      </div>

      {/* Content - Center */}
      <div
        style={{
          ...styles.contentContainer,
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? "translateY(20px)" : "translateY(0)",
        }}
      >
        {currentSlide?.content && (
          <p style={styles.content}>{currentSlide.content}</p>
        )}
      </div>

    </div>
  );
};

const styles = {
  container: {
    width: "100vw",
    height: "100vh",
    backgroundImage: "url('/custom/bg.png')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    userSelect: "none",
    cursor: "default",
  },
  imageContainer: {
    width: "100vw",
    height: "100vh",
    backgroundColor: "#000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    userSelect: "none",
    cursor: "default",
  },
  fullImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    transition: "opacity 0.5s ease-in-out",
  },
  titleContainer: {
    position: "absolute",
    top: "80px",
    left: 0,
    right: 0,
    textAlign: "center",
    padding: "0 40px",
    transition: "all 0.5s ease-in-out",
  },
  title: {
    fontSize: "5rem",
    fontWeight: "800",
    color: "#800020",
    margin: 0,
    textShadow: "0 2px 8px rgba(0,0,0,0.2)",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    lineHeight: "1.2",
    letterSpacing: "2px",
    whiteSpace: "pre-line",
  },
  contentContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "180px 60px 100px 60px",
    transition: "all 0.5s ease-in-out",
  },
  content: {
    fontSize: "3rem",
    fontWeight: "600",
    color: "#1a237e",
    margin: 0,
    textShadow: "0 2px 8px rgba(0,0,0,0.1)",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    lineHeight: "1.6",
    textAlign: "center",
    maxWidth: "90%",
    whiteSpace: "pre-line",
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#fff",
  },
  emptyIcon: {
    fontSize: "6rem",
    display: "block",
    marginBottom: "24px",
    color: "#FFD700",
    textShadow: "0 0 30px rgba(255,215,0,0.5)",
  },
  emptyTitle: {
    fontSize: "3rem",
    fontWeight: "700",
    margin: "8px 0",
    color: "#FFD700",
    textShadow: "0 4px 20px rgba(0,0,0,0.5)",
    fontFamily: "'Noto Sans Gurmukhi', 'Segoe UI', Arial, sans-serif",
  },
  emptyText: {
    fontSize: "1.5rem",
    color: "rgba(255,255,255,0.8)",
    marginTop: "24px",
  },
};

export default Slideshow;
